import { format } from "date-fns"
import { HistoryIcon, RotateCcwIcon } from "lucide-react"
import { useState } from "react"
import { usePostHog } from "posthog-js/react"
import { toast } from "sonner"
import { CommentContent } from "~/components/activity/Comments"
import { EntityIdentity } from "~/components/EntityIdentity"
import { posthogEnabled } from "~/components/providers/Posthog"
import { Button, TooltipButton } from "~/components/ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogClose,
	DialogTrigger
} from "~/components/ui/dialog"
import { difficultyLabel } from "~/lib/difficulty"
import { confirmAction } from "~/lib/native-dialog"
import { enumFormat } from "~/lib/utils"
import { api, type RouterOutputs } from "~/trpc/react"

export function IssueHistoryDialog({
	issue,
	canRestore,
	hasUnsavedChanges,
	saving
}: {
	issue: NonNullable<RouterOutputs["issues"]["get"]>
	canRestore: boolean
	hasUnsavedChanges: boolean
	saving: boolean
}) {
	const [open, setOpen] = useState(false)
	const [offset, setOffset] = useState(0)
	const [selectedId, setSelectedId] = useState<string | null>(null)
	const [reviewedAt, setReviewedAt] = useState(issue.updatedAt)
	const utils = api.useUtils()
	const posthog = usePostHog()
	const history = api.issues.history.useQuery(
		{ issueId: issue.id, teamId: issue.teamId, limit: 26, offset },
		{ enabled: open }
	)
	const selected =
		history.data?.find((entry) => entry.id === selectedId) ?? history.data?.[0]
	const restore = api.issues.restoreHistory.useMutation({
		onSuccess: async () => {
			setOpen(false)
			toast.success("Issue version restored")
			if (posthogEnabled)
				posthog.capture("issue_history_restored", {
					issue_id: issue.id,
					team_id: issue.teamId
				})
			await Promise.all([
				utils.issues.invalidate(),
				utils.favorites.list.invalidate(),
				utils.projects.list.invalidate(),
				utils.github.issuePullRequests.invalidate(),
				utils.user.getTeams.invalidate()
			])
		},
		onError: (error) => {
			toast.error(error.message)
			void utils.issues.get.invalidate()
		}
	})
	return (
		<Dialog
			open={open}
			onOpenChange={(value) => {
				if (restore.isPending) return
				setOpen(value)
				if (value) {
					setOffset(0)
					setSelectedId(null)
					setReviewedAt(issue.updatedAt)
					if (posthogEnabled)
						posthog.capture("issue_history_viewed", {
							issue_id: issue.id,
							team_id: issue.teamId
						})
				}
			}}
		>
			<DialogTrigger asChild>
				<TooltipButton variant="outline" size="icon-md" tooltip="Issue history">
					<HistoryIcon className="size-4" />
				</TooltipButton>
			</DialogTrigger>
			<DialogContent size="lg" layout="scroll">
				<DialogHeader variant="divided">
					<DialogTitle>Issue history</DialogTitle>
					<DialogDescription>
						Versions saved before each change. Restoring keeps comments, project
						links and blockers, and saves the current version first.
					</DialogDescription>
				</DialogHeader>
				<div className="overflow-y-auto px-6 min-h-0">
					{history.isLoading ? (
						<p role="status">Loading history…</p>
					) : history.isError ? (
						<div role="alert">
							<p>Could not load issue history.</p>
							<Button variant="outline" onClick={() => void history.refetch()}>
								Try again
							</Button>
						</div>
					) : !history.data?.length ? (
						<p>No saved versions yet.</p>
					) : (
						<div className="grid gap-6 md:grid-cols-[240px_minmax(0,1fr)]">
							<div className="flex flex-col gap-2">
								{history.data.slice(0, 25).map((entry) => (
									<Button
										key={entry.id}
										variant={selected?.id === entry.id ? "active" : "ghost"}
										className="h-auto justify-start whitespace-normal text-left"
										disabled={restore.isPending}
										aria-pressed={selected?.id === entry.id}
										onClick={() => setSelectedId(entry.id)}
									>
										<span className="flex flex-col gap-1">
											<span>
												{format(entry.createdAt, "d MMM yyyy, HH:mm:ss")}
											</span>
											<span className="text-xs text-text-sub">
												Before {entry.actor?.user.name ?? "a system"}’s change
											</span>
										</span>
									</Button>
								))}
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										disabled={offset === 0 || restore.isPending}
										onClick={() => {
											setOffset(offset - 25)
											setSelectedId(null)
										}}
									>
										Newer
									</Button>
									<Button
										variant="outline"
										size="sm"
										disabled={history.data.length <= 25 || restore.isPending}
										onClick={() => {
											setOffset(offset + 25)
											setSelectedId(null)
										}}
									>
										Older
									</Button>
								</div>
							</div>
							{selected && (
								<div className="min-w-0 flex flex-col gap-4">
									<EntityIdentity
										kind="user"
										name={selected.actor?.user.name ?? "System"}
										image={selected.actor?.user.image}
									/>
									<h3 className="text-lg font-semibold break-words">
										{selected.title}
									</h3>
									<dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
										<dt>Status</dt>
										<dd>{enumFormat(selected.status)}</dd>
										<dt>Priority</dt>
										<dd>{enumFormat(selected.priority)}</dd>
										<dt>Difficulty</dt>
										<dd>
											{difficultyLabel(
												selected.difficulty,
												issue.team.difficulties
											)}
										</dd>
										<dt>Due date</dt>
										<dd>
											{selected.dueDate
												? format(selected.dueDate, "d MMM yyyy")
												: "No due date"}
										</dd>
										<dt>Assignee</dt>
										<dd>
											{selected.assignee ? (
												<EntityIdentity
													kind="user"
													name={selected.assignee.orgMember.user.name}
													image={selected.assignee.orgMember.user.image}
												/>
											) : (
												"Unassigned"
											)}
										</dd>
										<dt>Labels</dt>
										<dd>
											{selected.labelIds
												.map(
													(id) =>
														issue.teamLabels.find((label) => label.id === id)
															?.name ?? "Deleted label"
												)
												.join(", ") || "None"}
										</dd>
									</dl>
									<div key={selected.id}>
										{selected.description ? (
											<CommentContent content={selected.description} />
										) : (
											<p className="text-text-sub">No description</p>
										)}
									</div>
								</div>
							)}
						</div>
					)}
				</div>
				<DialogFooter variant="divided">
					<DialogClose asChild>
						<Button variant="ghost" disabled={restore.isPending}>
							Close
						</Button>
					</DialogClose>
					{canRestore && (
						<div className="flex flex-col items-end gap-2">
							{hasUnsavedChanges && (
								<p className="text-sm text-text-sub">
									Save or discard your edits before restoring.
								</p>
							)}
							<Button
								disabled={
									!selected ||
									history.isError ||
									hasUnsavedChanges ||
									saving ||
									restore.isPending
								}
								onClick={async () => {
									if (!selected) return
									const confirmed = await confirmAction({
										title: "Restore issue version",
										message: `Restore the version before ${format(selected.createdAt, "d MMM yyyy, HH:mm:ss")}? This replaces the title, description, status, priority, difficulty, due date, assignee and labels. The current version will be saved in history. Comments and project links are kept.`
									})
									if (confirmed)
										restore.mutate({
											issueId: issue.id,
											teamId: issue.teamId,
											historyId: selected.id,
											updatedAt: reviewedAt
										})
								}}
							>
								<RotateCcwIcon className="size-4" />
								{restore.isPending ? "Restoring…" : "Restore this version"}
							</Button>
						</div>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
